import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────
// FamilyBandhan — Login screen v2 (LOCKED design, 13 Jun 2026)
// Visual layer replaced per locked mockup. OTP logic 100% unchanged.
//
// ── ONLINE SHUTDOWN NOTICE (19 Jul 2026) ──
// FamilyBandhan Online is temporarily paused (DPDP compliance decision —
// solo OPC founder, minimal org setup). Backend (familybandhan-backend on
// Cloud Run) has been deleted, Firestore data wiped, GCS bucket removed.
// This flag hides the login form and shows a "Coming Soon" placeholder
// instead. All OTP logic below is untouched and fully working — flip
// ONLINE_LOGIN_ENABLED back to true (and redeploy the backend) to relaunch.
// ─────────────────────────────────────────────────────────────────────────

const ONLINE_LOGIN_ENABLED = false;

const Login = () => {
  const { login } = useAuth();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);
  const handleSendOTP = async () => {
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post('https://familybandhan-backend-933326390640.asia-south1.run.app/api/auth/send-otp', { email: email.trim().toLowerCase() });
      setMessage(`OTP sent to ${email}`);
      setStep('otp');
      setResendCooldown(30);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setVerifying(true);
    try {
      const response = await axios.post('https://familybandhan-backend-933326390640.asia-south1.run.app/api/auth/verify-otp', { email: email.trim().toLowerCase(), otp });
      login(response.data.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    }
    setVerifying(false);
  };

  return (
    <div className="fb-page">
      <style>{css}</style>

      {/* ════════════════ LEFT PANEL ════════════════ */}
      <section className="fb-left">
        {/* corner mandala — faint heritage texture */}
        <svg className="fb-mandala fb-tl" viewBox="0 0 200 200" aria-hidden="true">
          <g fill="none" stroke="#D4A017" strokeWidth="0.8">
            <circle cx="100" cy="100" r="96" /><circle cx="100" cy="100" r="78" />
            <circle cx="100" cy="100" r="58" /><circle cx="100" cy="100" r="38" />
            <g id="fb-petal">
              <path d="M100 4 C 112 30, 112 50, 100 62 C 88 50, 88 30, 100 4 Z" />
            </g>
            {[30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
              <use key={deg} href="#fb-petal" transform={`rotate(${deg} 100 100)`} />
            ))}
          </g>
        </svg>
        <svg className="fb-mandala fb-br" viewBox="0 0 200 200" aria-hidden="true">
          <g fill="none" stroke="#D4A017" strokeWidth="0.8">
            <circle cx="100" cy="100" r="96" /><circle cx="100" cy="100" r="78" />
            <circle cx="100" cy="100" r="58" /><circle cx="100" cy="100" r="38" />
          </g>
        </svg>

        <div className="fb-horizon" aria-hidden="true" />

        <header className="fb-brand">
          <h1>Family<span className="fb-gold">Bandhan</span></h1>
          <div className="fb-rule"><span className="fb-tag">CONNECTING GENERATIONS</span></div>
        </header>

        <div className="fb-hero">
          <h2>Your family's story,<br />preserved <em>forever</em></h2>
          <p>
            Every family has heroes whose stories were never told.{' '}
            <strong>FamilyBandhan</strong> preserves them — across generations, beyond lifetime.
          </p>
        </div>

        {/* Banyan tree — gold line art, draws itself on load */}
        <svg className="fb-banyan" viewBox="0 0 320 340" aria-hidden="true">
          <path className="fb-grow" strokeWidth="2.4" d="M160 330 C158 290 156 250 160 215 C163 185 158 170 160 150" />
          <path className="fb-grow" strokeWidth="1.8" d="M160 215 C130 200 100 195 70 200 M160 215 C190 200 222 196 252 202" />
          <path className="fb-grow" strokeWidth="1.8" d="M160 178 C135 160 105 150 78 152 M160 178 C188 162 216 154 244 156" />
          <path className="fb-grow" strokeWidth="1.8" d="M160 150 C145 125 130 110 108 100 M160 150 C178 124 196 110 218 102" />
          <path className="fb-grow" strokeWidth="1.6" d="M160 150 C158 120 160 95 160 72" />
          <path className="fb-grow" strokeWidth="1.2" d="M96 168 C95 210 94 260 95 318 M226 172 C228 215 229 262 228 318 M132 130 C130 180 129 250 130 322 M192 132 C194 185 195 252 194 322" />
          <path className="fb-grow" strokeWidth="1.4" d="M40 330 C100 324 220 324 286 330" />
          <g className="fb-leaves">
            <circle cx="160" cy="62" r="4" style={{ animationDelay: '1.6s' }} />
            <circle cx="118" cy="92" r="3.4" style={{ animationDelay: '1.8s' }} />
            <circle cx="206" cy="94" r="3.4" style={{ animationDelay: '1.9s' }} />
            <circle cx="84" cy="146" r="3" style={{ animationDelay: '2.0s' }} />
            <circle cx="238" cy="150" r="3" style={{ animationDelay: '2.1s' }} />
            <circle cx="74" cy="196" r="2.6" style={{ animationDelay: '2.2s' }} />
            <circle cx="248" cy="198" r="2.6" style={{ animationDelay: '2.3s' }} />
            <circle cx="140" cy="80" r="2.4" style={{ animationDelay: '2.4s' }} />
            <circle cx="182" cy="82" r="2.4" style={{ animationDelay: '2.5s' }} />
          </g>
        </svg>

        <div className="fb-stats">
          <div className="fb-stat"><div className="fb-n">7 Generations</div><div className="fb-d">Paternal &amp; Maternal</div></div>
          <div className="fb-stat"><div className="fb-n">Legacy</div><div className="fb-d">Joint &amp; Extended</div></div>
          <div className="fb-stat"><div className="fb-n">Bharat</div><div className="fb-d">Built in, built for</div></div>
        </div>
        <div className="fb-left-foot">A family-first platform where bonds matter more than followers</div>
      </section>

      {/* ════════════════ RIGHT PANEL ════════════════ */}
      <section className="fb-right" style={{display:'flex', alignItems:'stretch'}}>
        {/* Form area */}
        <div style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'48px 32px 80px', position:'relative'}}>

        {/* kolam knot motif */}
        <svg className="fb-knot" viewBox="0 0 48 48" aria-hidden="true">
          <g fill="none" stroke="#D4A017" strokeWidth="1.6" strokeLinecap="round">
            <path d="M24 6 C34 14 34 22 24 24 C14 26 14 34 24 42 C34 34 34 26 24 24 C14 22 14 14 24 6 Z" />
            <circle cx="24" cy="24" r="2.4" fill="#D4A017" stroke="none" />
          </g>
        </svg>

        {ONLINE_LOGIN_ENABLED ? (
        <div className="fb-form-card">
            <h3>Start your family book</h3>
          <p className="fb-sub">
            {step === 'email'
              ? <>One account for your entire family.<br />No passwords — just a one-time code on your email.</>
              : <>Enter the 6-digit code sent to<br /><strong>{email}</strong></>}
          </p>

          {step === 'email' && (
            <>
              <div className="fb-field">
                <label htmlFor="fb-email">Email address</label>
                <input
                  id="fb-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
                />
              </div>
              {error && <p className="fb-error">{error}</p>}
              <button className="fb-cta" onClick={handleSendOTP} disabled={loading}>
                {loading ? <><span className="fb-spinner" /> Sending…</> : <>Send OTP&nbsp;<span className="fb-gold">→</span></>}
              </button>
            </>
          )}

          {step === 'otp' && (
            <>
              {message && <p className="fb-success">{message}</p>}
              <div className="fb-field">
                <label htmlFor="fb-otp">One-time code</label>
                <input
                  id="fb-otp"
                  className="fb-otp"
                  type="text"
                  inputMode="numeric"
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyOTP()}
                  maxLength={6}
                />
              </div>
              {error && <p className="fb-error">{error}</p>}
              <button className="fb-cta" onClick={handleVerifyOTP} disabled={verifying || loading}>
                {verifying ? <><span className="fb-spinner" /> Verifying…</> : <>Verify OTP&nbsp;<span className="fb-gold">→</span></>}
              </button>
              <button
                className="fb-back"
                onClick={handleSendOTP}
                disabled={resendCooldown > 0 || loading}
              >
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
              </button>
              <button
                className="fb-back"
                onClick={() => { setStep('email'); setError(''); setOtp(''); setResendCooldown(0); }}
              >
                ← Change email
              </button>
            </>
          )}

          <p className="fb-reassure"><b>Private by design.</b> Your family tree is visible only to you.</p>
        </div>
        ) : (
        <div className="fb-form-card">
          <h3>Coming Soon</h3>
          <p className="fb-sub">
            FamilyBandhan Online is being built.<br />
          </p>
        </div>
        )}

        <a href="/download" style={{
          display: 'block', marginTop: 16, fontSize: 13,
          color: '#5a4a66', textAlign: 'center', textDecoration: 'none',
          padding: '10px 16px', border: '1px solid #d8cfc0',
          borderRadius: 8, background: '#faf7f2',
          transition: 'border-color 0.2s',
        }}>
          Prefer offline? <strong style={{ color: '#1a0533' }}>Download Desktop App</strong> →
        </a>

        </div>{/* end form area */}

<footer className="fb-right-foot">
  © 2026 FamilyBandhan · <a href="/privacy" className="fb-link">Privacy Policy</a> · <a href="/terms" className="fb-link">Terms of Use</a> · <a href="/download" className="fb-link">Desktop App</a>
</footer>

        {/* Generation strip */}
        <div className="fb-gen-strip">
          <p className="fb-gen-label">7 GENERATIONS</p>
          <div className="fb-gen-item">Silent Generation</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Baby Boomers</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Gen X</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Millennials</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Gen Z</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Gen Alpha</div>
          <div className="fb-gen-arrow"><div className="fb-gen-line"/><div className="fb-gen-v">▼</div><div className="fb-gen-line"/></div>
          <div className="fb-gen-item">Gen Beta</div>
          <p className="fb-gen-foot">Your family<br/>spans them all</p>
        </div>

      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Styles — locked mockup CSS (familybandhan_landing_v2.html, 13 Jun 2026)
// Added: .fb-coming-soon-badge (new, small addition for the paused state)
// ─────────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');

.fb-page{
  --plum-deep:#0e0018; --plum-warm:#2b0a38; --plum-mid:#1a0526;
  --gold:#D4A017; --gold-soft:rgba(212,160,23,.55); --gold-faint:rgba(212,160,23,.16);
  --ivory:#F7F2E8; --ivory-deep:#EFE7D6; --ink:#1c0e26; --grey-warm:#8a8090;
  --serif:'Cormorant Garamond',Georgia,serif; --sans:'DM Sans',-apple-system,sans-serif;
  display:flex; min-height:100vh; font-family:var(--sans);
  background:var(--plum-deep); color:#fff; margin:0;
}
.fb-page *{box-sizing:border-box}
.fb-gold{color:var(--gold)}

/* ── LEFT PANEL ── */
.fb-left{
  flex:1.15; position:relative; overflow:hidden;
  background:
    radial-gradient(120% 90% at 85% -10%, var(--plum-warm) 0%, transparent 55%),
    radial-gradient(140% 110% at -20% 110%, #3a1030 0%, transparent 50%),
    linear-gradient(165deg, var(--plum-mid) 0%, var(--plum-deep) 60%);
  display:flex; flex-direction:column; padding:56px 64px 0;
}
.fb-mandala{position:absolute; width:460px; height:460px; opacity:.07; pointer-events:none}
.fb-tl{top:-180px; left:-180px}
.fb-br{bottom:-200px; right:-200px; transform:rotate(180deg)}
.fb-horizon{
  position:absolute; left:0; right:0; bottom:0; height:46%;
  background:radial-gradient(70% 90% at 50% 108%, rgba(212,160,23,.20) 0%, rgba(150,70,40,.10) 38%, transparent 70%);
  pointer-events:none;
}
.fb-brand{position:relative; z-index:2}
.fb-brand h1{font-family:var(--serif); font-weight:600; font-size:50px; line-height:1.05; letter-spacing:.5px; margin:0}
.fb-rule{display:flex; align-items:center; gap:14px; margin-top:10px}
.fb-rule::before,.fb-rule::after{content:""; height:1px; flex:0 0 56px; background:linear-gradient(90deg,transparent,var(--gold-soft))}
.fb-rule::after{background:linear-gradient(90deg,var(--gold-soft),transparent)}
.fb-tag{font-size:11.5px; letter-spacing:4.5px; color:var(--gold-soft); font-weight:500}
.fb-hero{position:relative; z-index:2; margin-top:9vh; max-width:520px}
.fb-hero h2{font-family:var(--serif); font-weight:500; font-size:clamp(34px,3.6vw,50px); line-height:1.14; color:#fff; margin:0}
.fb-hero h2 em{font-style:italic; color:var(--gold)}
.fb-hero p{margin:20px 0 0; font-size:15px; line-height:1.75; color:rgba(255,255,255,.52); max-width:430px; font-weight:300}
.fb-hero p strong{color:rgba(255,255,255,.78); font-weight:500}
.fb-banyan{position:absolute; z-index:1; bottom:152px; right:-30px; width:min(46%,430px); opacity:.9; pointer-events:none}
.fb-banyan path{stroke:var(--gold); fill:none}
.fb-leaves circle{fill:var(--gold); stroke:none}
@media (prefers-reduced-motion:no-preference){
  .fb-grow{stroke-dasharray:1200; stroke-dashoffset:1200; animation:fbGrow 2.8s ease-out .3s forwards}
  @keyframes fbGrow{to{stroke-dashoffset:0}}
  .fb-leaves circle{opacity:0; animation:fbBloom .8s ease-out forwards}
  @keyframes fbBloom{to{opacity:1}}
}
.fb-stats{position:relative; z-index:2; margin-top:auto; display:flex; border-top:1px solid rgba(212,160,23,.22)}
.fb-stat{flex:1; padding:22px 26px 20px; border-left:1px solid rgba(212,160,23,.18)}
.fb-stat:first-child{border-left:none; padding-left:0}
.fb-n{font-family:var(--serif); font-size:21px; color:var(--gold); font-weight:600; white-space:nowrap}
.fb-d{font-size:11.5px; color:rgba(255,255,255,.42); margin-top:4px; letter-spacing:.3px}
.fb-left-foot{position:relative; z-index:2; padding:16px 0 26px; font-family:var(--serif); font-style:italic; font-size:14.5px; color:rgba(212,160,23,.82)}

/* ── RIGHT PANEL ── */
.fb-right{
  flex:1; background:var(--ivory); color:var(--ink);
  display:flex; flex-direction:row; align-items:stretch;
  position:relative; padding:0;
  background-image:
    radial-gradient(80% 60% at 50% 0%, #fff 0%, transparent 60%),
    linear-gradient(180deg, var(--ivory) 0%, var(--ivory-deep) 100%);
}
.fb-knot{width:44px; height:44px; margin-bottom:26px; opacity:.85}
.fb-form-card{width:100%; max-width:400px; text-align:center}
.fb-eyebrow{font-size:11px; letter-spacing:3.5px; color:var(--gold); font-weight:600; filter:brightness(.85)}
.fb-form-card h3{font-family:var(--serif); font-size:38px; font-weight:600; color:var(--ink); margin:14px 0 0; line-height:1.1}
.fb-sub{font-size:14px; color:var(--grey-warm); margin:12px 0 0; line-height:1.6}
.fb-sub strong{color:#5a4a66}
.fb-coming-soon-badge{
  display:inline-flex; align-items:center; margin-top:24px; padding:9px 20px;
  border:1px solid rgba(212,160,23,.4); border-radius:20px;
  font-size:12.5px; font-weight:600; letter-spacing:.3px; color:#5a4a66;
  background:rgba(212,160,23,.06);
}
.fb-field{margin-top:30px; text-align:left}
.fb-field label{display:block; font-size:12px; font-weight:600; letter-spacing:.5px; color:#5a4a66; margin-bottom:8px}
.fb-field input{
  width:100%; padding:15px 18px; font-size:15px; font-family:var(--sans);
  border:1.5px solid #d8cfc0; border-radius:10px; background:#fff; color:var(--ink);
  outline:none; transition:border-color .2s, box-shadow .2s;
}
.fb-field input::placeholder{color:#b3a9b8}
.fb-field input:focus{border-color:var(--gold); box-shadow:0 0 0 3px var(--gold-faint)}
.fb-field input.fb-otp{font-size:26px; text-align:center; letter-spacing:12px; padding:13px 18px}
.fb-cta{
  width:100%; margin-top:18px; padding:16px;
  font-family:var(--sans); font-size:15px; font-weight:600; letter-spacing:.3px;
  color:#fff; background:linear-gradient(170deg,#23052f,var(--plum-deep));
  border:none; border-radius:10px; cursor:pointer;
  transition:transform .15s, box-shadow .2s, opacity .2s;
  box-shadow:0 4px 18px rgba(14,0,24,.25);
}
.fb-cta:hover:not(:disabled){transform:translateY(-1px); box-shadow:0 6px 22px rgba(14,0,24,.32)}
.fb-cta:focus-visible{outline:3px solid var(--gold); outline-offset:2px}
.fb-cta:disabled{opacity:.55; cursor:not-allowed}
.fb-spinner{
  display:inline-block; width:14px; height:14px; margin-right:2px;
  border:2px solid rgba(255,255,255,.35); border-top-color:#fff;
  border-radius:50%; vertical-align:middle;
  animation:fbSpin .7s linear infinite;
}
@keyframes fbSpin{to{transform:rotate(360deg)}}
.fb-back{
  width:100%; margin-top:12px; padding:10px; background:transparent; border:none;
  font-family:var(--sans); font-size:13px; color:var(--grey-warm); cursor:pointer;
}
.fb-back:hover{color:var(--ink)}
.fb-error{margin:14px 0 0; font-size:13px; color:#b3261e; text-align:left}
.fb-success{margin:14px 0 0; font-size:13px; color:#2d6a2d; text-align:left}
.fb-reassure{margin-top:16px; font-size:12px; color:var(--grey-warm); line-height:1.6}
.fb-reassure b{color:var(--grey-warm); font-weight:400}
.fb-right-foot{position:absolute; bottom:26px; left:0; right:0; text-align:center; font-size:11.5px; color:var(--grey-warm)}
.fb-link{border-bottom:1px dotted #c9bfae; cursor:pointer}
.fb-gen-strip{display:flex; flex-direction:column; align-items:center; justify-content:space-between; padding:24px 16px; border-left:1px solid rgba(139,120,100,0.2); min-width:110px; width:130px; height:100%;}
.fb-gen-label{font-size:10px; letter-spacing:2px; color:var(--grey-warm); margin-bottom:12px; text-align:center;}
.fb-gen-item{background:#1a0533; color:var(--gold); font-size:12px; font-weight:500; padding:6px 12px; border-radius:20px; text-align:center; width:100%; box-sizing:border-box;}
.fb-gen-item:nth-child(even){background:#2b0a38;}
.fb-gen-arrow{display:flex; flex-direction:column; align-items:center;}
.fb-gen-line{width:1px; height:7px; background:rgba(212,160,23,0.8);}
.fb-gen-v{color:var(--gold); font-size:8px; line-height:1; opacity:0.8;}
.fb-gen-foot{font-size:11px; color:var(--grey-warm); text-align:center; margin-top:12px; font-style:italic; line-height:1.4;}

/* ── MOBILE ── */
@media (max-width:880px){
  .fb-page{flex-direction:column}
  .fb-left{padding:40px 28px 0; min-height:auto}
  .fb-brand h1{font-size:38px}
  .fb-hero{margin-top:36px}
  .fb-hero h2{font-size:30px}
  .fb-banyan{position:relative; bottom:auto; right:auto; width:220px; margin:28px auto 0; display:block}
  .fb-stats{margin-top:30px}
  .fb-stat{padding:16px 10px 14px}
  .fb-n{font-size:15.5px}
  .fb-d{font-size:10.5px}
  .fb-stat:first-child{padding-left:0}
  .fb-right{padding:48px 24px 96px}
  .fb-form-card h3{font-size:32px}
  .fb-mandala{width:300px; height:300px}
}
`;

export default Login;